import React from "react";

const PROXY_LINE_STYLE = {
  fontSize: "0.78rem",
  lineHeight: 1.3,
  wordBreak: "break-all",
  overflowWrap: "anywhere",
  maxWidth: "100%",
};

function normalizeProxy(value) {
  return String(value || "").trim().toLowerCase();
}

function proxiesMatch(emailProxy, mlProxy) {
  const a = normalizeProxy(emailProxy);
  const b = normalizeProxy(mlProxy);
  if (!a || !b) return false;
  return a === b || b.includes(a) || a.includes(b);
}

function pickEmailProxy(connectedProxy, proxy) {
  const connected = normalizeProxy(connectedProxy);
  if (connected) return connectedProxy.trim();
  const assigned = normalizeProxy(proxy);
  if (assigned) return proxy.trim();
  return null;
}

function renderProxyLines({ emailProxy, mlProxyValue, hasEmailProxy, hasMlProxy, match }) {
  if (hasEmailProxy && hasMlProxy) {
    if (!match) {
      return (
        <>
          <span className="text-danger" title="Connected proxy">
            <strong>Proxy:</strong> {emailProxy}
          </span>
          <span className="text-danger" title="ML profile proxy">
            <strong>ML:</strong> {mlProxyValue}
          </span>
        </>
      );
    }
    return (
      <span className="text-success" title="Proxy matches ML profile">
        {mlProxyValue}
      </span>
    );
  }

  if (hasMlProxy) {
    return (
      <span title={`ML profile proxy`}>
        {mlProxyValue}
      </span>
    );
  }

  if (hasEmailProxy) {
    return (
      <span title="Connected proxy">
        {emailProxy}
      </span>
    );
  }

  return null;
}

/**
 * Staff advice views: email-matched proxy + optional ML proxy with profile/folder name.
 */
export default function ProxyDisplay({
  connectedProxy,
  proxy,
  mlProxy,
  mlFolderName,
  mlProfileName,
  simplifyOnMatch = false,
  className = "",
}) {
  const emailProxy = pickEmailProxy(connectedProxy, proxy);
  const hasEmailProxy = !!emailProxy;
  const mlProxyValue = String(mlProxy || "").trim();
  const hasMlProxy = !!normalizeProxy(mlProxyValue);
  const folderName = String(mlFolderName || "").trim();
  const profileName = String(mlProfileName || "").trim();
  const hasMlMeta = hasMlProxy || folderName || profileName;

  if (!hasEmailProxy && !hasMlMeta) {
    return <span className={className}>—</span>;
  }

  const match =
    hasEmailProxy && hasMlProxy ? proxiesMatch(emailProxy, mlProxyValue) : null;

  if (simplifyOnMatch && match) {
    return (
      <span
        className={`text-success ${className}`.trim()}
        style={PROXY_LINE_STYLE}
        title="Proxy matches ML profile"
      >
        {emailProxy}
      </span>
    );
  }

  const proxyLines = renderProxyLines({
    emailProxy,
    mlProxyValue,
    hasEmailProxy,
    hasMlProxy,
    match,
  });

  return (
    <div
      className={`d-flex flex-column gap-1 ${className}`.trim()}
      style={PROXY_LINE_STYLE}
    >
      {hasMlMeta ? (
        <div className="d-flex flex-column gap-1">
          {folderName && (
            <span className="text-truncate" title={`ML folder: ${folderName}`}>
              <strong>{folderName}:</strong>
            </span>
          )}
          {profileName && (
            <span className="text-truncate" title={`ML profile: ${profileName}`}>
              {profileName}
            </span>
          )}
          <br/>
          {proxyLines}
        </div>
      ) : (
        proxyLines
      )}
    </div>
  );
}

export function MlProfileStatus({ multilogin, mlProfile, className = "" }) {
  const checked = !!multilogin;
  const hasProfile = !!mlProfile;

  let label = null;
  let title = "";

  if (checked && !hasProfile) {
    label = "ML: No profile";
    title = "Multilogin is enabled but no ML profile exists for this email";
  } else if (!checked && hasProfile) {
    label = "ML: available";
    title = "ML profile exists but multilogin is not enabled";
  }

  if (!label) return null;

  return (
    <span className={`text-danger ${className}`.trim()} style={{ fontSize: "14px" }} title={title}>
      {label}
    </span>
  );
}
